import { File, Folder, Resource } from '@nextforce/api'
import { makeStyles, Text } from '@rneui/themed'
import { t } from 'app/i18l'
import { FileFolderSortOption } from 'file_folder_repository/types'
import {
  FilterItem,
  FiltersRow,
} from 'file_folder_repository/views/filters_row'
import {
  FileListItem,
  FolderListItem,
} from 'file_folder_repository/views/list_item'
import { ActivityIndicator, Keyboard, View } from 'react-native'
import { FlatList } from 'react-native-gesture-handler'
import SearchBar from 'shared/components/search_bar'

interface Props {
  resources: Resource[]
  onPressFile: (file: File) => void
  selectedFilterIndex: number
  setSelectedFilterIndex: (index: number) => void
  loading: boolean
  filterItems: FilterItem[]
  searchTerm: string
  setSearchTerm: (searchTerm: string) => void
  onPressFolder(folder: Folder): void
  onLongPressResource(resource: Resource): void
  sortOption: FileFolderSortOption
}

export const FileFolderSearchView = ({
  onPressFile,
  resources,
  selectedFilterIndex,
  setSelectedFilterIndex,
  loading,
  filterItems,
  onPressFolder,
  searchTerm,
  setSearchTerm,
  onLongPressResource,
  sortOption,
}: Props) => {
  const styles = useStyles()
  return (
    <View style={styles.container}>
      <View style={styles.searchBarContainer}>
        <FiltersRow
          items={filterItems}
          selectedIndex={selectedFilterIndex}
          onSelect={setSelectedFilterIndex}
        />
        <SearchBar
          onPressClear={() => setSearchTerm('')}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          autoFocus
        />
      </View>
      {loading ? (
        <View style={styles.loadingContainer}>
          <Text style={styles.loadingText}>{t('loadingRepositoryText')}</Text>
          <ActivityIndicator
            size={'large'}
            color={styles.loadingIndicator.color}
          />
        </View>
      ) : (
        <FlatList
          keyboardShouldPersistTaps="handled"
          onScrollBeginDrag={() => Keyboard.dismiss()}
          data={resources}
          keyExtractor={item => item.id + item.name}
          renderItem={({ item }) =>
            item.type === 'file' ? (
              <FileListItem
                key={item.id + item.name}
                file={item}
                onPress={onPressFile}
                onLongPress={onLongPressResource}
                showPath
                metaDataType={sortOption}
              />
            ) : (
              <FolderListItem
                key={item.id + item.name}
                folder={item}
                onPress={onPressFolder}
                onLongPress={onLongPressResource}
                metaDataType={sortOption}
                showPath
              />
            )
          }
          ListEmptyComponent={() => (
            <View style={styles.emptyComponent}>
              <Text style={styles.emptyComponentText}>
                {t('noFilesOrFoldersFound')}
              </Text>
            </View>
          )}
          contentContainerStyle={styles.fileFolderListContainer}
        />
      )}
    </View>
  )
}

const useStyles = makeStyles(theme => ({
  container: {
    flex: 1,
  },
  searchBarContainer: {
    marginHorizontal: 10,
    marginTop: 10,
    paddingBottom: 10,
  },
  breadcrumbsContainer: { padding: 10 },
  fileFolderListContainer: {
    paddingBottom: 10,
    flexGrow: 1,
    backgroundColor:
      theme.mode === 'dark' ? theme.colors.white : theme.colors.grey6,
  },
  pasteComponentContainer: {
    padding: 10,
    flexDirection: 'row',
    alignItems: 'stretch',
  },
  pasteButtonContainer: {
    borderRadius: 20,
    backgroundColor: theme.colors.primary,
    flex: 1,
    marginRight: 10,
    justifyContent: 'center',
  },
  loadingIndicator: {
    color: theme.colors.primary,
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 50,
  },
  loadingText: { textAlign: 'center', paddingBottom: 10 },
  emptyComponent: { flex: 1, justifyContent: 'center' },
  emptyComponentText: { textAlign: 'center' },
}))
